import {
  bscTestnet,
  mainnet,
  polygon,
  bsc,
  base,
  arbitrum,
  avalanche,
  linea,
  opBNB,
} from "viem/chains";
import { createPublicClient, http } from "viem";
import type { PrivyClientConfig } from "@privy-io/react-auth";
import { PrivyProvider } from "@privy-io/react-auth";
import { WagmiProvider, createConfig } from "@privy-io/wagmi";
import { QueryClient } from "@tanstack/react-query";
import Config from "@/config";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { SnackbarProvider } from "notistack";
import { AlertProvider } from "./AlertProvider";
import config from "@/config";
import { AuthProvider } from "./AuthProvider";

export const publicClient = createPublicClient({
  chain: bscTestnet,
  transport: http(),
});


export const wagmiConfig = createConfig({
  chains: [
    base,
    bscTestnet,
    mainnet,
    polygon,
    bsc,
    arbitrum,
    avalanche,
    linea,
    opBNB,
  ],
  transports: {
    [base.id]: http(config.RPC.TEST_LINK),
    [bsc.id]: http(),
    [polygon.id]: http(),
    [arbitrum.id]: http(),
    [avalanche.id]: http(),
    [linea.id]: http(),
    [bscTestnet.id]: http(),
    [opBNB.id]: http(),
    [mainnet.id]: http(),
  },
});

const privyConfig: PrivyClientConfig = {
  embeddedWallets: {
    createOnLogin: "users-without-wallets",
    requireUserPasswordOnCreate: true,
  },
  loginMethods: ["wallet", "email", "sms"],
  appearance: {
    showWalletLoginFirst: true,
  },
};

// Map to track CORS errors by query key to avoid showing multiple notifications
// and prevent retrying CORS error requests
const corsErrorQueryKeys = new Map<string, boolean>();
const serverErrorNotificationShown = false;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // If a query has previously failed with CORS, don't retry it
      retry: (_failureCount, error: any) => {
        const queryKey = JSON.stringify(error?.meta?.queryKey || "unknown");

        // If it's a CORS error, mark it and don't retry
        const isCorsError =
          error?.message?.includes("CORS") ||
          error?.name === "NetworkError" ||
          (error?.message &&
            /cross-origin|CORS policy|Access-Control-Allow/i.test(
              error?.message
            )) ||
          error?.code === "ERR_NETWORK";

        if (isCorsError) {
          corsErrorQueryKeys.set(queryKey, true);
          return false; // Don't retry CORS errors
        }

        // Check if this query has had a CORS error before
        if (corsErrorQueryKeys.has(queryKey)) {
          return false; // Don't retry if previous CORS error
        }

        return false; // We generally don't want to retry by default
      },
      refetchOnWindowFocus: false,
      staleTime: 1000 * 3,
      throwOnError: (error) => {
        // Throw on server errors (5xx)
        if ((error as any).response?.status >= 500) {
          // This will allow the error to propagate to the onError handler
          // where serverErrorEvent will be dispatched
          return true;
        }

        // Throw on CORS errors
        if (
          (error as any).message?.includes("CORS") ||
          (error as any).name === "NetworkError" ||
          ((error as any).message &&
            /cross-origin|CORS policy|Access-Control-Allow/i.test(
              (error as any).message
            ))
        ) {
          return true;
        }
        return false;
      },
    },
  },
  // queryCache: new QueryCache({
  //   onError: (error, _query) => {
  //     // Check for server errors (5xx)
  //     const isServerError = (error as any).response?.status >= 500;
  //     if (isServerError && !serverErrorNotificationShown) {
  //       // Prevent multiple notifications
  //       serverErrorNotificationShown = true;

  //       // Dispatch server error event
  //       serverErrorEvent.dispatchEvent(
  //         new CustomEvent("server-error", {
  //           detail: {
  //             message:
  //               "Server error occurred. Redirecting to maintenance page.",
  //             error,
  //           },
  //         })
  //       );

  //       // Reset the flag after some time
  //       setTimeout(() => {
  //         serverErrorNotificationShown = false;
  //       }, 10000);
  //     }
  //   },
  // }),
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

export default function Providers({ children }: { children: React.ReactNode }) {
  return (
    <PrivyProvider
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      appId={Config.NETWORK_CONFIG.PRIVY_APP_ID as string}
      config={privyConfig}
    >
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}
      >
        <AuthProvider>
          <SnackbarProvider
            maxSnack={1}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <AlertProvider>
              <WagmiProvider config={wagmiConfig} reconnectOnMount={false}>
                {children}
              </WagmiProvider>
            </AlertProvider>
          </SnackbarProvider>
        </AuthProvider>
      </PersistQueryClientProvider>
    </PrivyProvider>
  );
}
